import {
	SearchEngine,
	StandaloneSearchBox as HeadlessStandAloneSearchBox,
	SearchBox as HeadlessSearchBox
} from "@coveo/headless";
import parse from "html-react-parser";
import { useContext, useEffect, useRef, useState } from "react";
import HeaderSearchContext from "components/header/context/HeaderContentContext";
import SearchBoxRecommendations from "components/search/client/SearchBoxRecommendations";
import {
	standaloneSearchBoxStorageKey,
	CoveoHeadlessContext
} from "components/coveo-headless/types/coveo-headless-types";

import { registerSearchHub, launchAnalytics } from "components/coveo-headless/utils/initAnalytics";
import { ImageSearchMagnifyingGlass } from "components/utility/images";

interface SearchBoxProps {
	context?: CoveoHeadlessContext;
	controller: HeadlessStandAloneSearchBox | HeadlessSearchBox;
	engine?: SearchEngine;
	searchHub?: string;
}

const SearchBox = ({ controller, context, engine, searchHub }: SearchBoxProps) => {
	const searchBoxRef = useRef<HTMLDivElement | null>(null);
	const searchInputRef = useRef<HTMLInputElement | null>(null);

	const { openSearch, setOpenSearch } = useContext(HeaderSearchContext);
	const [state, setState] = useState(controller.state);
	const [cursor, setCursor] = useState(-1);
	const [inputValue, setInputValue] = useState<string | undefined>(undefined);
	const [onScrollClose, setOnScrollClose] = useState(false);
	const recommendations = window.recommendations;

	useEffect(() => {
		controller.subscribe(() => {
			setState(controller.state);
		});
	}, [controller]);

	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (searchBoxRef.current && !searchBoxRef.current.contains(e.target as Node)) {
				setOpenSearch!(false);
				searchInputRef.current?.blur();
			}
		};
		const handleScroll = () => {
			setOnScrollClose(true);
			setOpenSearch!(false);
			searchInputRef.current?.blur();
		};
		window.addEventListener("scroll", handleScroll);
		window.addEventListener("click", handleClickOutside);

		return () => {
			window.addEventListener("scroll", handleScroll);
			window.removeEventListener("click", handleClickOutside);
		};
	}, [setOpenSearch]);

	useEffect(() => {
		if (openSearch) {
			searchInputRef.current?.focus();
		}
	}, [openSearch]);

	if (engine && context && searchHub) {
		launchAnalytics(engine, context);
		registerSearchHub(engine, searchHub);

		if ("redirectTo" in state && state.redirectTo) {
			const { redirectTo, value, analytics } = state;
			const data = JSON.stringify({ value, analytics });
			localStorage.setItem(standaloneSearchBoxStorageKey, data);

			window.location.href = value ? `${redirectTo}#q=${value}` : redirectTo;

			return null;
		}
	}

	return (
		<div
			className="coveo-headless-searchbox"
			ref={searchBoxRef}
			role="button"
			tabIndex={0}
			onClick={(e) => {
				setOnScrollClose(false);
				const target = e.target as Element;
				if (
					target.classList.contains("coveo-headless-searchbox") ||
					target.classList.contains("coveo-headless-searchbox-input")
				) {
					setOpenSearch!(true);
				}
			}}
			onKeyDown={(e) => {
				if (e.key === "Spacebar") {
					setOpenSearch!(true);
				}
			}}
		>
			<ImageSearchMagnifyingGlass />
			<input
				ref={searchInputRef}
				className="coveo-headless-searchbox-input"
				placeholder={recommendations ? recommendations.placeholder : ""}
				type="text"
				value={inputValue ? inputValue : state.value}
				onChange={(e) => {
					controller.updateText(e.target.value);
					setCursor(-1);
					setInputValue(undefined);
					setOnScrollClose(false);
				}}
				onKeyDown={(e) => {
					if (e.key === "Enter") {
						inputValue ? controller.selectSuggestion(inputValue) : controller.submit();
						setOpenSearch!(false);
						searchInputRef.current?.blur();
					}
					if (e.key === "ArrowDown") {
						if (state.suggestions) {
							setCursor((prevCursor) =>
								prevCursor < state.suggestions.length - 1
									? prevCursor + 1
									: state.suggestions.length - 1 - prevCursor
							);
							setInputValue(
								state.suggestions[cursor === state.suggestions.length - 1 ? 0 : cursor + 1].rawValue
							);
						}
					}
					if (e.key === "ArrowUp") {
						if (state.suggestions) {
							setCursor((prevCursor) =>
								prevCursor >= 0
									? prevCursor === 0
										? state.suggestions.length - 1 + prevCursor
										: prevCursor - 1
									: state.suggestions.length + prevCursor
							);
							setInputValue(
								state.suggestions[
									cursor === -1 || cursor === 0 ? state.suggestions.length - 1 : cursor - 1
								].rawValue
							);
						}
					}
				}}
			/>
			{state.value !== "" && openSearch && !onScrollClose && (
				<div className="coveo-headless-searchbox-suggestions">
					<div className="coveo-headless-searchbox-suggestion-typed">
						<a className="body-3-left-medium-cool-gray" href={`/search#q=${state.value}`}>
							{inputValue ? inputValue : state.value}
						</a>
					</div>
					{state.suggestions.length > 0 && (
						<div className="coveo-headless-searchbox-suggestions-list" role="listbox">
							{state.suggestions.map((suggestion, i) => {
								const value = suggestion.rawValue;
								return (
									<div
										key={value}
										aria-label={value}
										aria-selected={cursor === i}
										className={`coveo-headless-searchbox-suggestion-item ${
											cursor === i ? "keySelected" : ""
										}`}
										role="option"
										tabIndex={0}
										onClick={() => {
											controller.selectSuggestion(value);
											setOpenSearch!(false);
											searchInputRef.current?.blur();
										}}
										onKeyDown={(e) => {
											if (e.key === "Enter") {
												controller.selectSuggestion(value);
												setOpenSearch!(false);
												searchInputRef.current?.blur();
											}
										}}
									>
										{parse(suggestion.highlightedValue)}
									</div>
								);
							})}
						</div>
					)}
				</div>
			)}
			{recommendations && state.value === "" && openSearch && !onScrollClose && (
				<SearchBoxRecommendations
					content={recommendations.content}
					data={recommendations.data}
					setOpenSearch={setOpenSearch}
				/>
			)}
		</div>
	);
};

export default SearchBox;
